// Billing adapter: the four calls the page makes, plus the plain-English mapping
// of gateway states. Endpoints are per-user (src/routes/billing.js).
//
// The gateway's checkout runs in ITS script, loaded from sdk.cashfree.com on this
// page only (the CSP for /billing allows that one host). We hand it a session id
// and it takes the customer to the bank; we never see a card number or UPI id.

import { signedOutUrl } from './gate.js';

const CASHFREE_SDK ='https://sdk.cashfree.com/js/v3/cashfree.js';
const RAZORPAY_SDK = 'https://checkout.razorpay.com/v1/checkout.js';

async function req(method, path, body) {
  const r = await fetch(path, {
    method, credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401) { window.location.replace(signedOutUrl()); throw new Error('Signed out'); }
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
  return data;
}

export const loadStatus    = () => req('GET', '/api/billing/status');

/** Whether they have ever connected a broker account. Any failure reads as "no" (the card just shows). */
export async function hasConnectedBroker() {
  try {
    const r = await fetch('/api/connections/all', { credentials: 'include' });
    if (!r.ok) return false;
    const { connections = [] } = await r.json();
    return connections.some((c) => c.connected || c.lastConnectedAt);
  } catch (_) { return false; }
}

/** A verified Indian mobile as the 10 digits the phone fields take, else ''. */
export const localTen = (e164) => { const m = /^\+91(\d{10})$/.exec(String(e164 || '')); return m ? m[1] : ''; };
/** The account's verified mobile, to pre-fill the phone fields ('' when none or on any failure). */
export async function loadVerifiedPhone() {
  try {
    const r = await fetch('/api/account/phone', { credentials: 'include' });
    return r.ok ? localTen((await r.json()).phone) : '';
  } catch (_) { return ''; }
}

/**
 * A code typed in the promo box might be an OFFER code (offers.js) rather than a discount.
 * Resolves the offer if it was applied, null if the code is not an offer (so the caller prices it
 * as a discount), and throws with a sentence to show if it is an offer they cannot take.
 */
export async function applyOffer(code) {
  const r = await fetch('/api/billing/offer', {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code }),
  });
  if (r.status === 404) return null;
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.message || 'Could not apply that code');
  return d.offer;
}
export const createMandate = ({ phone, name, method, billingState }) => req('POST', '/api/billing/mandate', { phone, name, method, billingState });
export const syncMandate   = () => req('POST', '/api/billing/mandate/sync');
// A plan purchase waiting on its autopay set-up (the gateway's page was closed, or they want to pay another way).
export const checkSetup    = () => req('POST', '/api/billing/setup/check');
export const startOverSetup = () => req('POST', '/api/billing/setup/start-over');
export const cancelMandate = () => req('POST', '/api/billing/mandate/cancel');
// The legacy one-time order route. The plan checkout does NOT use this — it quotes a plan and
// pays that order (plans.api.js). Kept because the route still exists and its own test covers
// it; delete both together when the legacy billing page goes.
export const createOrder   = ({ amount, phone, note }) => req('POST', '/api/billing/order', { amount, phone, note });
// Razorpay Checkout hands the browser a signed result. Confirming it server-side lets the page say
// "paid" at once instead of waiting on the webhook — which still arrives and is still the truth.
export const verifyPayment = (r) => req('POST', '/api/billing/verify-payment', r);

// ── the gateway's checkout ─────────────────────────────────────────────────
// Each gateway ships its own script; the page loads only the configured one.
const _pending = {};
function loadScript(src, globalName) {
  if (window[globalName]) return Promise.resolve(window[globalName]);
  if (_pending[globalName]) return _pending[globalName];
  _pending[globalName] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src; s.async = true;
    s.onload = () => (window[globalName] ? resolve(window[globalName]) : reject(new Error('Payment script did not load')));
    s.onerror = () => reject(new Error('Could not load the payment script — check your connection and try again'));
    document.head.appendChild(s);
  }).finally(() => { delete _pending[globalName]; });
  return _pending[globalName];
}

const LANDING = (subscriptionId) => '/billing?mandate=' + encodeURIComponent(subscriptionId);

/**
 * Take the customer to approve the mandate. Cashfree redirects the tab; Razorpay opens
 * a modal and calls back, after which we land on the same return URL ourselves.
 * `checkout` is whatever the server returned for the configured provider.
 */
export async function openMandateCheckout({ provider, checkout, mode, subscriptionId, prefill }) {
  if (!checkout) throw new Error('This setup can no longer be resumed. Start a new one.');
  if (provider === 'razorpay') {
    return openRazorpay({ ...checkout, prefill, description: 'Zensibly monthly — payment mandate', recurring: true,
      onDone: () => window.location.assign(LANDING(subscriptionId)) });
  }
  const Cashfree = await loadScript(CASHFREE_SDK, 'Cashfree');
  const cf = Cashfree({ mode: mode === 'production' ? 'production' : 'sandbox' });
  const result = await cf.subscriptionsCheckout({ subsSessionId: checkout.sessionId, redirectTarget: '_self' });
  if (result && result.error) throw new Error(result.error.message || 'The payment page could not open');
}

/** One-time payment. Used by the plan checkout in PlanPicker via BillingContainer.onPay. */
export async function openOrderCheckout({ provider, checkout, mode, orderId, prefill, onVerify }) {
  if (provider === 'razorpay') {
    return openRazorpay({ ...checkout, prefill, description: 'Zensibly', recurring: false,
      onVerify,
      onDone: () => window.location.assign('/billing?order=' + encodeURIComponent(orderId)) });
  }
  const Cashfree = await loadScript(CASHFREE_SDK, 'Cashfree');
  const cf = Cashfree({ mode: mode === 'production' ? 'production' : 'sandbox' });
  const result = await cf.checkout({ paymentSessionId: checkout.sessionId, redirectTarget: '_self' });
  if (result && result.error) throw new Error(result.error.message || 'The payment page could not open');
}

// Razorpay Checkout.js: a modal. Resolves when the customer completes (then onDone
// navigates), rejects when they close it, so the page can hand the button back.
async function openRazorpay({ keyId, orderId, customerId, amount, prefill, description, recurring, onDone, onVerify }) {
  const Razorpay = await loadScript(RAZORPAY_SDK, 'Razorpay');
  return new Promise((resolve, reject) => {
    const rz = new Razorpay({
      key: keyId, order_id: orderId,
      ...(customerId ? { customer_id: customerId } : {}),
      ...(recurring ? { recurring: '1' } : {}),
      ...(amount ? { amount, currency: 'INR' } : {}),
      name: 'Zensibly', description, prefill: prefill || {}, theme: { color: '#6D5AE0' },
      // Checkout hands back the three signed fields. They were being discarded, so a successful
      // payment was only ever confirmed when the webhook arrived. Pass them up to be verified;
      // a failure here is not fatal, because the webhook will still settle it.
      handler: async (resp) => {
        try { if (onVerify && resp) await onVerify(resp); } catch (_) { /* webhook remains the truth */ }
        try { onDone(); } finally { resolve(); }
      },
      modal: { ondismiss: () => reject(new Error('Setup was closed before it finished')) },
    });
    rz.open();
  });
}

// ── plain English for what the gateway says ───────────────────────────────
export const INR = (n) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

export function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch (_) { return String(s); }
}

/** Mandate lifecycle → one line the reader is standing inside. */
export function mandateLine(m) {
  if (!m) return null;
  switch (m.status) {
    case 'ACTIVE': return m.billingActive ? 'Billing is on.' : 'Your mandate is set up. You are not being charged.';
    case 'INITIALIZED': return 'Setup started — your bank is waiting for your approval.';
    case 'BANK_APPROVAL_PENDING': return 'Your bank is still approving the mandate. This usually takes a few hours.';
    case 'ON_HOLD': return 'Your bank has paused this mandate. Set up a new one to continue.';
    case 'CANCELLED': return 'This mandate is cancelled. Nothing more will be charged.';
    case 'COMPLETED': return 'This mandate has ended.';
    default: return `Mandate status: ${m.status}`;
  }
}

/** Why billing started, in the reader's words. */
export function triggerLine(reason) {
  if (!reason) return '';
  if (reason.startsWith('rule_fired')) return 'started the day one of your rules stopped you';
  if (reason.startsWith('manual')) return 'switched on for your account';
  return reason;
}

/** What has to happen before the first charge, given the configured trigger. */
export function offerLine(trigger, offer) {
  if (offer) {
    const price = '₹' + Number(offer.priceInr).toLocaleString('en-IN', { minimumFractionDigits: Number.isInteger(offer.priceInr) ? 0 : 2, maximumFractionDigits: 2 });
    const per = offer.product === 'annual' ? 'a year' : 'a month';
    if (offer.kind === 'trial') {
      return `${offer.planName} free for ${offer.days} days, from the day your bank confirms. Then ${price} ${per} with GST. Cancel before the trial ends and nothing is taken.`;
    }
    return `${offer.planName} free until your daily loss limit or trade cap stops you, or for ${offer.days} days, whichever comes first, from the day your bank confirms. Then ${price} ${per} with GST. Cancel the autopay and the free plan ends.`;
  }
  if (trigger === 'rule_fired') return 'You start paying the day one of your rules stops you. Until then, free.';
  return 'You will not be charged until billing is switched on for your account.';
}

export const PAYMENT_STATUS = { PENDING: 'Scheduled', SUCCESS: 'Paid', FAILED: 'Failed', CANCELLED: 'Cancelled' };
/**
 * What to call a payment's state, in the customer's terms. "Scheduled" is right for a charge that
 * will be debited on a date; an unpaid one-time order is not scheduled, it is simply not paid yet.
 * Anything unrecognised reads as "Pending" rather than leaking a gateway's own word onto the page.
 */
export function paymentStatusLabel(p) {
  const s = p && p.status;
  if (s === 'PENDING') return p && p.kind === 'order' ? 'Not paid yet' : 'Scheduled';
  return PAYMENT_STATUS[s] || 'Pending';
}
export function paymentReason(p) {
  if (!p.reason) return p.kind === 'order' ? 'One-time payment' : 'Monthly';
  if (p.reason.startsWith('rule_fired')) return 'First month — a rule stopped you';
  if (p.reason.startsWith('manual')) return 'First month';
  if (p.reason === 'monthly') return 'Monthly';
  return p.reason;
}
